import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Snackbar, Alert, IconButton, Dialog, DialogContent, Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { AppSnackbar } from '../../types/snackbar';

type SnackbarContextValue = {
  enqueueSnackbar: (snack: Omit<AppSnackbar, 'id'> & { id?: string }) => string;
  closeSnackbar: (id?: string) => void;
};

const SnackbarContext = createContext<SnackbarContextValue | undefined>(undefined);

export const useSnackbar = () => {
  const ctx = useContext(SnackbarContext);
  if (!ctx) throw new Error('useSnackbar must be used within <AppSnackbarProvider>');
  return ctx;
};

export const AppSnackbarProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [queue, setQueue] = useState<AppSnackbar[]>([]);
  const [current, setCurrent] = useState<AppSnackbar | null>(null);
  const idCounterRef = useRef(0);

  // Dequeue the next item whenever current becomes empty and queue has items
  React.useEffect(() => {
    if (!current && queue.length > 0) {
      const [first, ...rest] = queue;
      setCurrent(first);
      setQueue(rest);
    }
  }, [current, queue]);

  const enqueueSnackbar = useCallback<SnackbarContextValue['enqueueSnackbar']>((snack) => {
    const id = snack.id ?? `snk_${++idCounterRef.current}`;
    const normalized: AppSnackbar = {
      id,
      message: snack.message,
      severity: snack.severity ?? "info",
      // persist:true snackbars must never auto-close via autoHideDuration
      autoHideDuration: snack.persist ? null : (snack.autoHideDuration ?? 3000),
      persist: !!snack.persist,
      persistAcrossRoutes: snack.persistAcrossRoutes ?? true,
    };
    setQueue((q) => [...q, normalized]);
    return id;
  }, []);

  const closeSnackbar = useCallback<SnackbarContextValue['closeSnackbar']>((id) => {
    if (!id) {
      setCurrent(null);
      return;
    }
    setCurrent((cur) => (cur?.id === id ? null : cur));
    setQueue((q) => q.filter((s) => s.id !== id));
  }, []);

  const handleClose = useCallback((_e?: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setCurrent(null);
  }, []);

  const ctxValue = useMemo(() => ({ enqueueSnackbar, closeSnackbar }), [enqueueSnackbar, closeSnackbar]);
React.useEffect(() => {
  const handleRouteChange = () => {
    setCurrent((cur) => {
      if (!cur) return cur;

      // 👇 KEY: DO NOT close when user navigates
      if (cur.persistAcrossRoutes) return cur;

      // otherwise close normally
      return null;
    });
  };

  window.addEventListener("popstate", handleRouteChange);

  return () => {
    window.removeEventListener("popstate", handleRouteChange);
  };
}, []);
  const isError = current?.severity === 'error';

  return (
    <SnackbarContext.Provider value={ctxValue}>
      {children}

      {/* Centered modal for error messages */}
      <Dialog
        open={!!current && isError}
        onClose={undefined}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'visible',
            boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              backgroundColor: '#d32f2f',
              borderRadius: '12px',
              padding: '24px 28px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              position: 'relative',
            }}
          >
            <ErrorOutlineIcon sx={{ color: '#fff', fontSize: '32px', mt: '2px', flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                Error
              </Typography>
              <Typography sx={{ color: '#fff', fontSize: '0.95rem', lineHeight: 1.6 }}>
                {current?.message}
              </Typography>
            </Box>
            <IconButton
              size="small"
              aria-label="close"
              onClick={() => closeSnackbar(current?.id)}
              sx={{
                color: '#fff',
                position: 'absolute',
                top: '10px',
                right: '10px',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Top-center snackbar for non-error messages */}
      {!isError && (
        <Snackbar
          key={current?.id}
          open={!!current}
          onClose={handleClose}
          autoHideDuration={current?.autoHideDuration}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            variant="filled"
            severity={current?.severity ?? 'info'}
            onClose={current?.persist ? undefined : handleClose}
            action={
              <IconButton size="small" aria-label="close" color="inherit" onClick={() => closeSnackbar(current?.id)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            }
            sx={{ width: '100%' }}
          >
            {current?.message}
          </Alert>
        </Snackbar>
      )}
    </SnackbarContext.Provider>
  );
};
